import { DocumentRevisionStatus } from '@/shared/lib';
import type { RecordAudit } from '@/entities/Record';

/**
 * One row of `GET /matrices/latest-revisions`: the newest revision of one record
 * code. In practice its status is `CURRENT` and, when the record was
 * inactivated, `INACTIVE`.
 *
 * The previous revisions are NOT here: `hasOlderRevisions` says whether the row
 * has any, and they are fetched on demand when the arrow is clicked.
 */
export interface RecordResponse {
  id: number;
  code: string;
  title: string | null;
  revision: number;
  status: DocumentRevisionStatus;
  effectiveDate: string | null;
  indexing: string | null;
  generatedRecordsCount: number;
  hasOlderRevisions: boolean;
  olderRevisionsCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: RecordAudit['createdBy'];
  updatedBy: RecordAudit['updatedBy'];
}

/**
 * One row of `GET /matrices/:id/previous-revisions`, which carries only the
 * fields an expanded row renders. In practice these revisions are `OBSOLETE` and,
 * when the revision was inactivated, `INACTIVE`.
 */
export interface RecordPreviousRevisionResponse {
  id: number;
  revision: number;
  status: DocumentRevisionStatus;
  /** The title that revision carried, which a rename makes different from the current one. */
  title: string | null;
  indexing: string | null;
  generatedRecordsCount: number;
}

/** A previous revision of a record, as the expansion of the row renders it. */
export interface RecordRevisionListRow {
  id: number;
  revision: number;
  revisionStatus: DocumentRevisionStatus;
  title: string | null;
  indexing: string | null;
  generatedRecordsCount: number;
}

export interface RecordListRow {
  id: number;
  code: string;
  title: string | null;
  indexing: string | null;
  revision: number;
  revisionStatus: DocumentRevisionStatus;
  isActive: boolean;
  generatedRecordsCount: number;
  /**
   * Whether the arrow belongs on the row. It comes from the listing, so the
   * screen does not have to fetch the previous revisions of every row to find
   * out which rows can expand.
   */
  hasOlderRevisions: boolean;
  audit: RecordAudit;
}

export function toRecordRevisionRows(
  revisions: RecordPreviousRevisionResponse[]
): RecordRevisionListRow[] {
  return revisions.map((revision) => ({
    id: revision.id,
    revision: revision.revision,
    revisionStatus: revision.status,
    title: revision.title,
    indexing: revision.indexing,
    generatedRecordsCount: revision.generatedRecordsCount,
  }));
}

export function toRecordListRow(record: RecordResponse): RecordListRow {
  const title = record.title?.trim() ?? '';

  return {
    id: record.id,
    code: record.code,
    title: title.length > 0 ? title : null,
    indexing: record.indexing,
    revision: record.revision,
    revisionStatus: record.status,
    // An inactivated record is one whose newest revision was inactivated: the
    // status carries that, so there is no separate flag to read.
    isActive: record.status !== DocumentRevisionStatus.INACTIVE,
    generatedRecordsCount: record.generatedRecordsCount,
    hasOlderRevisions: record.hasOlderRevisions,
    audit: {
      createdAt: record.createdAt,
      createdBy: record.createdBy,
      updatedAt: record.updatedAt,
      updatedBy: record.updatedBy,
    },
  };
}

/**
 * The ids are what the selects pre-select; the names keep a member or a cargo
 * visible after it stops being current. `date` is an instant, unlike the two
 * vigências of the response, which are civil dates. See DATE_CONVENTIONS.md.
 */
export interface RecordHomologationStage {
  memberId: number | null;
  memberName: string | null;
  positionId: number | null;
  positionName: string | null;
  date: string | null;
}

/**
 * Narrower than the two decisions instead of the same type with `positionId`
 * pinned to null: no column exists, so the field would promise what data cannot.
 */
export interface RecordHomologationElaboration {
  memberId: number | null;
  memberName: string | null;
  date: string | null;
}

/**
 * `elaboration` is never null: while the declared columns are empty the backend
 * answers with the creator of the revision and its creation date. `approval` and
 * `verification` are null until the revision goes through the matching queue.
 */
export interface RecordHomologationResponse {
  id: number;
  code: string;
  title: string | null;
  revision: number | null;
  elaboration: RecordHomologationElaboration;
  approval: RecordHomologationStage | null;
  verification: RecordHomologationStage | null;
  effectiveDate: string | null;
  previousRevisionEffectiveDate: string | null;
}

/** Body of `PATCH /matrices/:id/homologation`: only what changed is sent. */
export interface RecordHomologationPayload {
  elaborationMemberId?: number;
  /** No `elaborationPositionId`: the elaboration stage has no cargo. */
  elaboratedAt?: string;
  approvalMemberId?: number;
  approvalPositionId?: number;
  approvedAt?: string;
  verificationMemberId?: number;
  verificationPositionId?: number;
  verifiedAt?: string;
  effectiveDate?: string;
}
